// utils/logger.js
import { auth } from "../services/FireBase";
import { functions } from "../services/FireBase";
import { httpsCallable } from "firebase/functions";

const isDev = process.env.NODE_ENV !== "production";

// Simple in-memory dedupe (best effort)
const lastSent = new Map();

/**
 * Returns true if we should send this fingerprint again.
 *
 * @param {string} key
 * @param {number} timeoutMs
 * @return {boolean}
 */
function shouldSend(key, timeoutMs = 5000) {
  const now = Date.now();
  const prev = lastSent.get(key) || 0;

  if (now - prev > timeoutMs) {
    lastSent.set(key, now);
    return true;
  }
  return false;
}

/**
 * Normalizes Error-like objects.
 *
 * @param {*} err
 * @return {{name: string, code: string, message: string, stack: string}}
 */
function normalizeError(err) {
  if (!err) {
    return { name: "Error", code: "", message: "Unknown error", stack: "" };
  }

  return {
    name: String(err.name || "Error"),
    code: String(err.code || ""),
    message: String(err.message || err),
    stack: String(err.stack || ""),
  };
}

/**
 * Safe serializer.
 *
 * @param {*} obj
 * @return {*}
 */
function toPlain(obj) {
  try {
    if (obj instanceof Error) {
      return { name: obj.name, message: obj.message, stack: obj.stack };
    }
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    return String(obj);
  }
}

/**
 * Writes a log entry via Cloud Function (best effort).
 *
 * @param {"error"|"warn"|"info"} kind
 * @param {*} message
 * @param {*} context
 * @return {Promise<void>}
 */
async function write(kind, message, context) {
  const uid = auth?.currentUser?.uid || null;

  const norm = message instanceof Error ? normalizeError(message) : null;
  const msg = norm ? norm.message : String(message);

  const payload = {
    kind,
    message: msg,
    stack: norm ? norm.stack : (context?.stack ? String(context.stack) : null),
    code: norm ? norm.code : (context?.code ? String(context.code) : null),
    page: typeof window !== "undefined" ? window.location.pathname : null,
    uid,
    context: toPlain(context),
  };

  const fingerprint = `${kind}:${payload.message}`;
  if (!shouldSend(fingerprint)) {
    if (isDev) {
      console[kind === "error" ? "error" : kind](
        `[${kind}] ${msg}`,
        context,
      );
    }
    return;
  }

  try {
    const logClientError = httpsCallable(functions, "logClientError");
    // orgId NIET meegeven: function pakt orgId uit claims
    await logClientError({
      message: payload.message,
      stack: payload.stack,
      code: payload.code,
      page: payload.page,
      fingerprint,
      context: payload.context,
    });
  } catch (e) {
    // Logging must never break the app
    if (isDev) {
      console.error("Failed to call logClientError", e);
    }
  }

  if (isDev) {
    console[kind === "error" ? "error" : kind](
      `[${kind}] ${msg}`,
      context,
    );
  }
}

export const logger = {
  error: (msg, ctx = {}) => write("error", msg, ctx),
  warn: (msg, ctx = {}) => write("warn", msg, ctx),
  info: (msg, ctx = {}) => write("info", msg, ctx),
};
